import { useState, useEffect } from "react";
import moment from "moment";
import InputSelect from "../../../../../shared/components/inputs/InputSelect/InputSelect";
import InputText from "../../../../../shared/components/inputs/InputText/InputText";

//MUI
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import "./ModalSaida.scss";
import {
  opcoesDeCaixa,
  caixaParaMetodo,
  metodoParaCaixa,
} from "../../../../../shared/util/caixaEMetodo";

function ModalPagarSaida({
  OpenModalPagarSaida,
  handleCloseModalPagarSaida,
  sendRequest,
  updateSaidas,
  dadosModalPagarSaida,
  auth,
  clinicaId,
}) {
  const { startLoading, stopLoading } = useLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(OpenModalPagarSaida);
  const [dataPagamento, setDataPagamento] = useState(moment());
  const formasPagamento = [
    "Dinheiro",
    "Cheque",
    "Cheque Visado",
    "Transferência de crédito",
    "Internet banking",
    "Cartão do banco",
    "Débito direto da conta bancária",
    "Cartão de crédito",
    "Cartão de débito",
  ];
  const [metodoPagamento, setMetodoPagamento] = useState(formasPagamento[0]);
  const [comprovativo, setComprovativo] = useState();
  const [errorMessage, setErrorMessage] = useState();

  /* A caixa só era decidida ao criar a saída, e no dia do pagamento já não se
     podia mudar — mas é no pagamento que se sabe de onde o dinheiro saiu. */
  const [caixas, setCaixas] = useState([]);
  const [caixa, setCaixa] = useState(
    dadosModalPagarSaida.caixa?.id || dadosModalPagarSaida.caixa?._id || "",
  );

  //Cheque
  const [banco, setBanco] = useState("");
  const [nrContaCliente, setNrContaCliente] = useState("");
  const [nrCheque, setNrCheque] = useState("");
  const [dataCheque, setDataCheque] = useState(null);

  useEffect(() => {
    setOpenModal(OpenModalPagarSaida);
  }, [OpenModalPagarSaida]);

  useEffect(() => {
    const fetchCaixas = async () => {
      try {
        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setCaixas(
          resposta.caixas.map((cai) => ({ id: cai.id, value: cai.caixa })),
        );
      } catch (err) {
        console.error("err", err);
      }
    };
    fetchCaixas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId]);

  /* Método e caixa andam juntos. Ver `shared/util/caixaEMetodo`. */
  const metodoPagamentoChangeHandler = (valor) => {
    setMetodoPagamento(valor);
    setCaixa((atual) => caixaParaMetodo(caixas, valor, atual));
  };

  const caixaChangeHandler = (novaCaixa) => {
    setCaixa(novaCaixa);
    setMetodoPagamento((atual) =>
      metodoParaCaixa(caixas, novaCaixa, atual, formasPagamento),
    );
  };

  const guardarSaidaHandler = async () => {
    startLoading();
    try {
      const formData = new FormData();
      formData.append("dataPagamento", dataPagamento);
      formData.append("metodoPagamento", metodoPagamento);
      formData.append("pagaPor", auth.userId);
      if (caixa) formData.append("caixa", caixa);

      if (metodoPagamento === "Cheque" || metodoPagamento === "Cheque Visado") {
        formData.append("banco", banco);
        formData.append("nrContaCliente", nrContaCliente);
        formData.append("nrCheque", nrCheque);
        formData.append("dataCheque", dataCheque);
      }

      if (comprovativo) {
        const modifiedFile = new File(
          [comprovativo],
          `${clinicaId}-${comprovativo.name}`,
          { type: comprovativo.type },
        );
        formData.append("comprovativo", modifiedFile);
      }
      //LEMBRA DE ADICIONAR COMPROVATIVO MAIS TARDE
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/despesas/pagar/${dadosModalPagarSaida.id}`,
        "PATCH",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      dadosModalPagarSaida.metodoPagamento = metodoPagamento;
      dadosModalPagarSaida.dataPagamento = dataPagamento;
      /* A caixa pode ter mudado no pagamento: sem isto a lista continuava a
         mostrar a antiga até se recarregar o ecrã. */
      const caixaEscolhida = caixas.find((c) => c.id === caixa);
      if (caixaEscolhida) {
        dadosModalPagarSaida.caixa = {
          id: caixaEscolhida.id,
          caixa: caixaEscolhida.value,
        };
      }
      dadosModalPagarSaida.despesaPaga = true;
      dadosModalPagarSaida.pagaPor = { _id: auth.userId, name: auth.nome };

      if (metodoPagamento === "Cheque") {
        dadosModalPagarSaida.dataCheque = dataCheque;
        dadosModalPagarSaida.nrCheque = nrCheque;
        dadosModalPagarSaida.banco = banco;
        dadosModalPagarSaida.nrContaCliente = nrContaCliente;
      }

      updateSaidas(dadosModalPagarSaida, "atualizar");
      toast.success("Saída paga com sucesso!");
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmitting(false);
      stopLoading();
      handleCloseModalPagarSaida();
    }
  };

  const preGuardarSaidaHandler = () => {
    if (!dataPagamento) {
      toast.error("Introduza a data de pagamento!");
    } else if (!moment.isMoment(dataPagamento) || !dataPagamento.isValid()) {
      toast.error("Introduza a data de pagamento corretamente!");
    } else if (
      (metodoPagamento === "Cheque" || metodoPagamento === "Cheque Visado") &&
      (!nrContaCliente || nrContaCliente.trim() === "")
    ) {
      toast.error("Introduza o número da conta do cliente");
    } else if (
      (metodoPagamento === "Cheque" || metodoPagamento === "Cheque Visado") &&
      (!nrCheque || nrCheque.trim() === "")
    ) {
      toast.error("Introduza o número do cheque");
    } else if (
      (metodoPagamento === "Cheque" || metodoPagamento === "Cheque Visado") &&
      !dataCheque
    ) {
      toast.error("Introduza a data do cheque");
    } else if (
      (metodoPagamento === "Cheque" || metodoPagamento === "Cheque Visado") &&
      (!moment.isMoment(dataCheque) || !dataCheque.isValid())
    ) {
      toast.error("Introduza a data do cheque corretamente");
    } else if (!isSubmitting) {
      setIsSubmitting(true);
      guardarSaidaHandler();
    }
  };

  const handleFileChange = (event) => {
    // Check file size (1MB limit)
    const maxSizeInBytes = 1 * 1024 * 1024; // 1MB in bytes
    const file = event.target.files[0];
    // Check file extension
    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.pdf)$/i;
    if (!allowedExtensions.test(file.name)) {
      setErrorMessage(`O ficheiro "${file.name}" tem uma extensão inválida.`);
    } else if (file.size > maxSizeInBytes) {
      setErrorMessage(
        `O ficheiro "${file.name}" excede o tamanho máximo permitido (1MB).`,
      );
    } else {
      setErrorMessage("");
      setComprovativo(file);
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={handleCloseModalPagarSaida}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className="fluxo-atendimento__modal__box adicionar-despesa__modal"
          id="modal__adicionar-despesa"
        >
          <h1 className="fluxo-atendimento__modal__titulo">Pagar saída</h1>
          <div className="adicionar-despesa__modal__container">
            <div className="customInputContainer adicionar-despesa__modal__container--span2">
              <input
                type="text"
                defaultValue={dadosModalPagarSaida.descricao}
                className="customInput inputNumber"
                id="descricao"
                style={{ pointerEvents: "none", color: "#88959e" }}
                readOnly
              />
              <label className="customInputLabel" htmlFor="descricao">
                Descrição
              </label>
            </div>

            <div className="customInputContainer adicionar-despesa__modal__container--span1">
              <input
                type="text"
                defaultValue={dadosModalPagarSaida.valor}
                className="customInput inputNumber"
                id="valor"
                style={{ pointerEvents: "none", color: "#88959e" }}
                readOnly
              />
              <label className="customInputLabel" htmlFor="valor">
                Valor
              </label>
            </div>

            <LocalizationProvider
              dateAdapter={AdapterMoment}
              className="adicionar-despesa__modal__container--span1"
            >
              <DesktopDatePicker
                className="valorDataCaixa__fields--field muiDatePicker"
                label="Data de vencimento*"
                inputFormat="DD-MM-YYYY"
                value={dadosModalPagarSaida.dataLimitePagamento}
                renderInput={(params) => <TextField {...params} />}
                disabled
                readOnly
                onChange={() => {}}
              />
            </LocalizationProvider>

            {dadosModalPagarSaida.fornecedor && (
              <div className="customInputContainer adicionar-despesa__modal__container--span2">
                <input
                  type="text"
                  defaultValue={
                    dadosModalPagarSaida.fornecedor.nome || "Sem fornecedor"
                  }
                  className="customInput inputNumber"
                  id="fornecedor"
                  style={{ pointerEvents: "none", color: "#88959e" }}
                  readOnly
                />
                <label className="customInputLabel" htmlFor="fornecedor">
                  Fornecedor
                </label>
              </div>
            )}

            {dadosModalPagarSaida.cliente && (
              <div className="customInputContainer adicionar-despesa__modal__container--span2">
                <input
                  type="text"
                  defaultValue={
                    dadosModalPagarSaida.cliente.nome || "Sem cliente"
                  }
                  className="customInput inputNumber"
                  id="cliente"
                  style={{ pointerEvents: "none", color: "#88959e" }}
                  readOnly
                />
                <label className="customInputLabel" htmlFor="cliente">
                  Cliente
                </label>
              </div>
            )}

            {dadosModalPagarSaida.funcionario && (
              <div className="customInputContainer adicionar-despesa__modal__container--span2">
                <input
                  type="text"
                  defaultValue={dadosModalPagarSaida.funcionario.nome || ""}
                  className="customInput inputNumber"
                  id="funcionario"
                  style={{ pointerEvents: "none", color: "#88959e" }}
                  readOnly
                />
                <label className="customInputLabel" htmlFor="funcionario">
                  Funcionário
                </label>
              </div>
            )}

            {dadosModalPagarSaida.funcionarioUser && (
              <div className="customInputContainer adicionar-despesa__modal__container--span2">
                <input
                  type="text"
                  defaultValue={dadosModalPagarSaida.funcionarioUser.name || ""}
                  className="customInput inputNumber"
                  id="funcionarioUser"
                  style={{ pointerEvents: "none", color: "#88959e" }}
                  readOnly
                />
                <label className="customInputLabel" htmlFor="funcionarioUser">
                  Funcionário
                </label>
              </div>
            )}

            <div className="customInputContainer adicionar-despesa__modal__container--span2">
              <input
                type="text"
                defaultValue={dadosModalPagarSaida.categoria.categoria}
                className="customInput inputNumber"
                id="categoria"
                style={{ pointerEvents: "none", color: "#88959e" }}
                readOnly
              />
              <label className="customInputLabel" htmlFor="categoria">
                Categoria
              </label>
            </div>

            <div className="customInputContainer adicionar-despesa__modal__container--span1">
              <select
                onChange={(e) => caixaChangeHandler(e.target.value)}
                className="customInputSelect"
                value={caixa}
                id="caixa"
              >
                {opcoesDeCaixa(caixas, metodoPagamento, caixa).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.value}
                  </option>
                ))}
              </select>
              <label className="customInputLabelSelect" htmlFor="caixa">
                Caixa*
              </label>
            </div>
            <textarea
              cols="30"
              rows="7"
              placeholder="observacao"
              value={dadosModalPagarSaida.observacao}
              className="adicionar-despesa__modal__container__textarea"
              readOnly
              style={{ color: "#88959e" }}
            ></textarea>
          </div>

          <div
            className="adicionar-despesa__modal__pagamento"
            style={{ marginTop: 25, marginBottom: 25 }}
          >
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              className="adicionar-despesa__modal__container--span1"
            >
              <DesktopDatePicker
                className="valorDataCaixa__fields--field muiDatePicker"
                label="Data do Pagamento*"
                inputFormat="DD-MM-YYYY"
                value={dataPagamento}
                onChange={(value) => setDataPagamento(value)}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <InputSelect
              label="Forma de pagamento"
              initialValue={metodoPagamento}
              handleChange={metodoPagamentoChangeHandler}
              items={formasPagamento}
            />
            {(metodoPagamento === "Cheque" ||
              metodoPagamento === "Cheque Visado") && (
              <div
                style={{
                  gridColumn: "1/-1",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                  gap: "15px",
                  marginTop: "25px",
                }}
              >
                <InputText
                  className="adicionar-despesa__modal__container--span2"
                  label="Banco"
                  initialValue={banco}
                  handleChange={setBanco}
                  id="bancoId"
                />
                <InputText
                  className="adicionar-despesa__modal__container--span2"
                  label="Nº Conta Cliente *"
                  initialValue={nrContaCliente}
                  handleChange={setNrContaCliente}
                  id="nrContaClienteID"
                />
                <InputText
                  className="adicionar-despesa__modal__container--span1"
                  label="Nº Cheque *"
                  initialValue={nrCheque}
                  handleChange={setNrCheque}
                  id="NrChequeID"
                />
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DesktopDatePicker
                    className="valorDataCaixa__fields--field muiDatePicker"
                    label="Data do Cheque *"
                    inputFormat="DD-MM-YYYY"
                    value={dataCheque}
                    onChange={(value) => setDataCheque(value)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </div>
            )}
          </div>

          {comprovativo && (
            <div className="despesas--comprovativo-selecionado">
              Comprovativo Selecionado: {comprovativo.name}
            </div>
          )}

          <div>
            <input
              type="file"
              style={{ display: "none" }}
              id="fileInput"
              onChange={handleFileChange}
            />
            <label htmlFor="fileInput" className="anexar-comprovativo">
              {comprovativo ? "Substituir comprovativo" : "Anexar comprovativo"}
            </label>
          </div>

          <div className="despesas--regras-ficheiro">
            <span className="modalAdicionarFicheiros__container-adicionar--regras-ficheiro">
              O Tamanho máximo do upload é 1MB
            </span>
            <span className="modalAdicionarFicheiros__container-adicionar--regras-ficheiro">
              Formatos aceites: .pdf, .png, .jpg e .jpeg
            </span>
          </div>

          {errorMessage && (
            <div className="modal-mensagem-de-erro">
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="fluxo-atendimento__modal__bottom_btns">
            <span
              className="cancel-btn"
              onClick={handleCloseModalPagarSaida}
              style={{ display: "block" }}
            >
              Fechar
            </span>

            <span
              disabled={isSubmitting || errorMessage}
              className="blue-button"
              onClick={preGuardarSaidaHandler}
            >
              {isSubmitting ? "Pagando..." : "Pagar"}
            </span>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default ModalPagarSaida;
